import _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiConfigs } from '../../../api/APIConfigs';
import { BibleStudy } from '../../../api/models/BibleStudy';
import { Person } from '../../../api/models/Person';
import { RhEvent } from '../../../api/models/RhEvent';
import { UploadResult } from '../../../api/models/UploadResult';
import { bibleStudySlice } from '../../../model/bibleStudySlice';
import { RootState } from '../../../model/store';
import { routes } from '../../../route-list';
import { extractStartEndDates } from '../../../util/date-utilities';
import { useAsyncRequest } from '../../../util/use-async-request';
import { useRequest } from '../../../util/use-request';
import { validateEventFormState } from '../../../util/validator';
import { RhConfirmModal } from '../../core/RhConfirmModal';
import { RhSelectOption } from '../../core/RhSelect';
import { EventFormScreen } from '../components/EventFormScreen';

export type EventFormProps = {
  // Inputs
  /** if form is being saved to the be */
  isSaving: boolean;
  /** the current form */
  form: EventFormState;
  /** if user wants to edit title and desc or not */
  isEditingTitle: boolean;
  /** whether or not the programs modal is open */
  isProgramsModalOpen: boolean;
  /** if user is still getting bible studies */
  isLoadingPrograms: boolean;
  /** the list of bible studies the user would have to choose from */
  bibleStudies: BibleStudy[];
  /** lst of event leaders for the dropdown */
  eventLeaders: RhSelectOption[];
  /** loading event leaders on mount */
  isLoadingEventLeaders: boolean;

  // Inputs
  /** called when the user clicks the cancel button */
  userClickedCancel: () => void;
  /** called when user clicked save */
  userClickedSave: () => void;
  /** user wants to edit title AND description */
  userClickedEditTitle: () => void;
  /** called when the user edits the fields */
  userUpdatedForm: (formState: EventFormState) => void;
  /** user wants to close the event form programs modal */
  userToggledProgramsModal: () => void;
  /** user choses a program to attach to the event */
  userSelectedProgram: (program: BibleStudy) => void;
  /** user wants to add a tag to tags */
  userPushedToTags: () => void;
};

export type EventFormState = {
  title: string;
  description: string;
  tags: string[];
  leaderId: string;
  publishingStatus?: string;
  // we need these to be nullable
  startTime: Date | null;
  endTime: Date | null;
  // fields that stay in the form (not in model)
  tag: string;
  program: BibleStudy | null;
  scheduledDate: Date | null;
  introVideo?: File;
  vimeoEventUrl: string;
};

export const initialEventFormState: EventFormState = {
  startTime: null,
  endTime: null,
  title: '',
  description: '',
  tags: [],
  leaderId: '',
  publishingStatus: 'unpublished',
  // form fields
  tag: '',
  program: null,
  scheduledDate: null,
  introVideo: undefined,
  vimeoEventUrl: '',
};

export const EventFormContainer = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<EventFormState>(initialEventFormState);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isProgramsModalOpen, setIsProgramsModalOpen] = useState(false);
  const [eventLeaders, setEventLeaders] = useState<RhSelectOption[]>([]);
  const bibleStudies = useSelector((state: RootState) => state.bibleStudy.list);

  const request = useRequest();
  const dispatch = useDispatch();
  const history = useHistory();

  // get list of bible study programs
  const { loading: isLoadingPrograms } = useAsyncRequest(
    useCallback(() => request<BibleStudy[]>(apiConfigs.getBibleStudies({ showOnlyFromCurrentUser: false })), [request]),
    useCallback(
      bibleStudies => {
        dispatch(bibleStudySlice.actions.receivedList({ bibleStudies }));
      },
      [dispatch]
    )
  );

  const { loading: isLoadingEventLeaders } = useAsyncRequest(
    useCallback(() => request<Person[]>(apiConfigs.getEventLeaders()), [request]),
    useCallback(leaders => {
      setEventLeaders(
        leaders.map((person: Person) => ({
          label: _.startCase(person.name),
          value: person.id,
        }))
      );
    }, [])
  );

  return (
    <EventFormScreen
      form={form}
      bibleStudies={bibleStudies}
      eventLeaders={eventLeaders}
      isSaving={isSaving}
      isEditingTitle={isEditingTitle}
      isProgramsModalOpen={isProgramsModalOpen}
      isLoadingPrograms={isLoadingPrograms}
      isLoadingEventLeaders={isLoadingEventLeaders}
      userClickedCancel={() => {
        // Check first if the form is pristine. If pristine, proceed without confirmation.
        if (_.isEqual(form, initialEventFormState)) {
          history.push(routes.EVENTS);
        } else {
          confirmAlert({
            customUI: options => (
              <RhConfirmModal
                description="Are you sure you want to exit without saving?"
                isOpen={true}
                onClose={options.onClose}
                onOk={() => {
                  options.onClose();
                  history.push(routes.EVENTS);
                }}
              />
            ),
          });
        }
      }}
      userClickedSave={async () => {
        // this is outside of [validateEventFormState] bc we need to check the original
        // values for [scheduledDate], [startTime] and [endTime]
        if (!form.scheduledDate) {
          toast.error('Event cannot be saved without a Scheduled Date');
          return;
        }
        if (!form.startTime || !form.endTime) {
          toast.error('Event cannot be saved without a Start Time and End Time');
          return;
        }

        const { startTime, endTime } = extractStartEndDates({
          scheduledDate: form.scheduledDate,
          startTime: form.startTime,
          endTime: form.endTime,
        });

        // more form checks
        const formError = validateEventFormState({ ...form, startTime, endTime });
        if (formError) {
          toast.error(formError);
          return;
        }

        let introVideoMuxId = null;
        if (form.introVideo) {
          try {
            setIsSaving(true);
            const uploaded = await request<UploadResult>(apiConfigs.uploadVideo(form.introVideo));
            introVideoMuxId = uploaded.muxID;
            setIsSaving(false);
          } catch (e: any) {
            setIsSaving(false);
          }
        }

        // you may notice some `!` (non-null assertion)
        // please see validateEventFormState function

        try {
          setIsSaving(true);
          const payload = {
            programId: form.program!.id,
            startTime,
            endTime,
            title: form.title,
            description: form.description,
            featuredImage: {
              moduleType: 'image' as const,
              sortOrder: 0,
              url: form.program!.imageUrl,
              name: null,
              description: null,
              type: null,
            },
            tags: form.tags,
            leaderId: form.leaderId,
            introMuxId: introVideoMuxId,
            publishingStatus: form.publishingStatus ?? 'published',
            vimeoEventUrl: form.vimeoEventUrl,
            // no need to populate this
            muxID: null,
          };
          const result = await request<RhEvent>(apiConfigs.addEvent(payload));
          setIsSaving(false);

          if (form.introVideo && !result.introMuxId) {
            toast.info('Event created but intro video failed to upload.');
            history.push(routes.EVENTS);
          } else {
            toast.success('Event created!');
            history.push(routes.EVENTS);
          }
          setForm(initialEventFormState);
          setIsSaving(false);
        } catch (e: any) {
          toast.error(e.message || 'Failed to create event');
          setIsSaving(false);
        }
      }}
      userClickedEditTitle={() => {
        setIsEditingTitle(!isEditingTitle);
      }}
      userUpdatedForm={setForm}
      userToggledProgramsModal={() => {
        setIsProgramsModalOpen(!isProgramsModalOpen);
      }}
      userSelectedProgram={program => {
        setForm(prev => ({ ...prev, program }));
        setIsProgramsModalOpen(false);
      }}
      userPushedToTags={() => {
        // validate tags for duplicates
        if (form.tags.findIndex(tag => form.tag.toLowerCase() === tag.toLowerCase()) !== -1) {
          toast.error(`Duplicate tag "${form.tag}"`);
          return;
        }
        setForm(prev => ({
          ...prev,
          tags: [...form.tags, form.tag],
          tag: '',
        }));
      }}
    />
  );
};
